import React, { useState } from 'react';
import {
    ArrowLeft, Mic, Loader2, Lightbulb, Trophy, CheckCircle2,
    XCircle, SkipForward, MessageCircle, Award, RefreshCw, Send
} from 'lucide-react';
import { generateAIContent, getPrompts } from '../aiService';
import { useSubscription } from '../contexts/SubscriptionContext';

const PERSONAS = [
    {
        id: 'hr',
        name: 'HR Recruiter',
        emoji: '🤝',
        description: 'Friendly screen — culture fit, motivation, communication.',
        color: 'rose'
    },
    {
        id: 'technical',
        name: 'Technical Lead',
        emoji: '💻',
        description: 'Deep technical questions tied to the skills on your resume.',
        color: 'blue'
    },
    {
        id: 'behavioral',
        name: 'Behavioral Interviewer',
        emoji: '🎭',
        description: 'STAR-method scenarios about past work.',
        color: 'purple'
    },
    {
        id: 'scenario',
        name: 'Hiring Manager',
        emoji: '🧭',
        description: 'Hypothetical "what would you do if…" scenarios.',
        color: 'amber'
    },
    {
        id: 'deep-dive',
        name: 'Senior Engineer',
        emoji: '🔍',
        description: 'Drills into one project from your resume.',
        color: 'emerald'
    },
    {
        id: 'leader',
        name: 'Director / VP',
        emoji: '🎯',
        description: 'Leadership, strategy, and big-picture judgement.',
        color: 'indigo'
    }
];

const COLOR_CLASSES = {
    rose: 'border-rose-300 bg-rose-50 hover:bg-rose-100',
    blue: 'border-blue-300 bg-blue-50 hover:bg-blue-100',
    purple: 'border-purple-300 bg-purple-50 hover:bg-purple-100',
    amber: 'border-amber-300 bg-amber-50 hover:bg-amber-100',
    emerald: 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100',
    indigo: 'border-indigo-300 bg-indigo-50 hover:bg-indigo-100'
};

const MAX_QUESTIONS = 5;

const parseJsonResponse = (text) => {
    const cleaned = text.replace(/```json/g, '').replace(/```/g, '').trim();
    return JSON.parse(cleaned);
};

const MockInterview = ({ resumeData, apiConfig, onBack, openAIModal }) => {
    const { hasFeature, purchaseFeature, getFeaturePrice } = useSubscription();
    const isUnlocked = hasFeature('mock_interview');

    const [persona, setPersona] = useState(null);
    const [jobDescription, setJobDescription] = useState('');
    const [stage, setStage] = useState('setup'); // setup | asking | answering | reviewing | summary
    const [currentQ, setCurrentQ] = useState(null);
    const [answer, setAnswer] = useState('');
    const [history, setHistory] = useState([]); // [{ question, answer, feedback, score, competency, modelAnswer }]
    const [feedback, setFeedback] = useState(null);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const hasApiKey = apiConfig && (apiConfig.gemini || apiConfig.openai || apiConfig.anthropic || apiConfig.groq || apiConfig.deepseek);

    const requestNextQuestion = async (currentHistory) => {
        setLoading(true);
        setError(null);
        try {
            const prompt = getPrompts('mock-interview-question', {
                persona,
                resume: resumeData,
                jobDescription,
                history: currentHistory
            });
            const raw = await generateAIContent(apiConfig, prompt);
            const parsed = parseJsonResponse(raw);
            setCurrentQ(parsed);
            setStage('answering');
            setAnswer('');
            setFeedback(null);
        } catch (e) {
            setError(e.message);
            setStage('setup');
        } finally {
            setLoading(false);
        }
    };

    const handleStart = () => {
        if (!isUnlocked) {
            purchaseFeature('mock_interview');
            return;
        }
        if (!hasApiKey) {
            openAIModal();
            return;
        }
        setHistory([]);
        setSummary(null);
        requestNextQuestion([]);
    };

    const submitAnswer = async (isSkip = false) => {
        setLoading(true);
        setError(null);
        try {
            const prompt = getPrompts('mock-interview-score', {
                persona,
                question: currentQ.question,
                competency: currentQ.competency,
                answer: isSkip ? '' : answer,
                resume: resumeData
            });
            const raw = await generateAIContent(apiConfig, prompt);
            const parsed = parseJsonResponse(raw);
            setFeedback(parsed);
            const entry = {
                question: currentQ.question,
                competency: currentQ.competency,
                answer: isSkip ? '' : answer,
                feedback: parsed.oneLineImprovement,
                score: parsed.score,
                modelAnswer: parsed.modelAnswer,
                strengths: parsed.strengths,
                weaknesses: parsed.weaknesses,
                competencyScores: parsed.competencyScores
            };
            setHistory(prev => [...prev, entry]);
            setStage('reviewing');
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleNext = () => {
        if (history.length >= MAX_QUESTIONS) {
            generateSummary();
        } else {
            requestNextQuestion(history);
        }
    };

    const generateSummary = async () => {
        setStage('summary');
        setLoading(true);
        try {
            const prompt = getPrompts('mock-interview-summary', {
                persona,
                session: history
            });
            const raw = await generateAIContent(apiConfig, prompt);
            setSummary(parseJsonResponse(raw));
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    // Aggregate competency scores across session for radar
    const competencyAverages = (() => {
        const acc = {};
        const counts = {};
        history.forEach(h => {
            Object.entries(h.competencyScores || {}).forEach(([k, v]) => {
                if (v === null || v === undefined) return;
                acc[k] = (acc[k] || 0) + v;
                counts[k] = (counts[k] || 0) + 1;
            });
        });
        return Object.keys(acc).reduce((out, k) => {
            out[k] = acc[k] / counts[k];
            return out;
        }, {});
    })();

    // -- Setup screen --
    if (stage === 'setup') {
        return (
            <div className="min-h-screen bg-slate-100">
                <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-4">
                    <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg">
                        <ArrowLeft size={20} />
                    </button>
                    <div className="flex items-center gap-2">
                        <Mic className="text-purple-600" size={20} />
                        <h1 className="font-bold text-slate-800">Mock Interview</h1>
                        {!isUnlocked && (
                            <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">
                                ${getFeaturePrice('mock_interview') || 5} to unlock
                            </span>
                        )}
                    </div>
                </header>

                <div className="max-w-3xl mx-auto p-6">
                    <h2 className="text-2xl font-bold text-slate-800 mb-2">Pick your interviewer</h2>
                    <p className="text-sm text-slate-500 mb-6">
                        Each persona asks different questions and grades differently. Run multiple sessions to find your weak spots.
                    </p>

                    <div className="grid md:grid-cols-2 gap-3 mb-6">
                        {PERSONAS.map(pp => (
                            <button
                                key={pp.id}
                                onClick={() => setPersona(pp)}
                                className={`text-left p-4 rounded-xl border-2 transition-colors ${persona?.id === pp.id
                                        ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                                        : COLOR_CLASSES[pp.color]
                                    }`}
                            >
                                <div className="text-2xl mb-1">{pp.emoji}</div>
                                <h3 className="font-bold text-slate-800 text-sm">{pp.name}</h3>
                                <p className="text-xs text-slate-600 mt-1">{pp.description}</p>
                            </button>
                        ))}
                    </div>

                    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
                        <label className="block text-xs font-bold text-slate-600 uppercase mb-2">
                            Job description (optional, for more targeted Qs)
                        </label>
                        <textarea
                            value={jobDescription}
                            onChange={(e) => setJobDescription(e.target.value)}
                            rows={4}
                            placeholder="Paste the JD here…"
                            className="w-full text-sm border border-slate-200 rounded-lg p-2 outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                        />
                    </div>

                    <button
                        onClick={handleStart}
                        disabled={!persona || loading}
                        className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2"
                    >
                        {loading ? <Loader2 size={18} className="animate-spin" /> : <Mic size={18} />}
                        {isUnlocked ? `Start ${MAX_QUESTIONS}-question session` : `Unlock for $${getFeaturePrice('mock_interview') || 5}`}
                    </button>

                    {error && <p className="text-xs text-red-600 mt-3">{error}</p>}
                </div>
            </div>
        );
    }

    // -- Summary screen --
    if (stage === 'summary') {
        return (
            <div className="min-h-screen bg-slate-100">
                <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-4">
                    <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg">
                        <ArrowLeft size={20} />
                    </button>
                    <div className="flex items-center gap-2">
                        <Trophy className="text-amber-500" size={20} />
                        <h1 className="font-bold text-slate-800">Session Report</h1>
                    </div>
                </header>

                <div className="max-w-4xl mx-auto p-6">
                    {loading && (
                        <div className="bg-white rounded-xl p-8 text-center">
                            <Loader2 className="animate-spin text-purple-600 mx-auto mb-3" size={32} />
                            <p className="text-sm text-slate-500">Compiling your report…</p>
                        </div>
                    )}

                    {summary && (
                        <>
                            <div className="bg-white rounded-xl shadow-sm p-6 mb-4 text-center">
                                <div className={`inline-block px-6 py-2 rounded-full text-white font-bold mb-3 ${summary.verdict === 'Hire' ? 'bg-emerald-500' :
                                        summary.verdict === 'Borderline' ? 'bg-amber-500' : 'bg-red-500'
                                    }`}>
                                    {summary.verdict}
                                </div>
                                <div className="text-5xl font-black text-slate-800">{summary.overallScore}<span className="text-2xl text-slate-400">/100</span></div>
                                <p className="text-sm text-slate-500 mt-2">overall session score</p>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4 mb-4">
                                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                                    <h3 className="font-bold text-emerald-800 mb-2 flex items-center gap-1"><CheckCircle2 size={16} /> Top strengths</h3>
                                    <ul className="text-sm text-emerald-900 space-y-1">
                                        {summary.topStrengths?.map((s, i) => <li key={i}>• {s}</li>)}
                                    </ul>
                                </div>
                                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                                    <h3 className="font-bold text-red-800 mb-2 flex items-center gap-1"><XCircle size={16} /> Areas to work on</h3>
                                    <ul className="text-sm text-red-900 space-y-1">
                                        {summary.topGaps?.map((s, i) => <li key={i}>• {s}</li>)}
                                    </ul>
                                </div>
                            </div>

                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                                <h3 className="font-bold text-blue-800 mb-2 flex items-center gap-1"><Lightbulb size={16} /> Next prep focus</h3>
                                <p className="text-sm text-blue-900">{summary.nextPrepFocus}</p>
                            </div>

                            {/* Competency bars (radar substitute — no chart lib dep) */}
                            <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
                                <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-1"><Award size={16} /> Competency profile</h3>
                                {Object.entries(competencyAverages).map(([k, v]) => (
                                    <div key={k} className="mb-2">
                                        <div className="flex justify-between text-xs mb-1">
                                            <span className="capitalize text-slate-600">{k.replace('-', ' ')}</span>
                                            <span className="font-mono text-slate-500">{v.toFixed(1)}/10</span>
                                        </div>
                                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                            <div className="h-full bg-purple-500" style={{ width: `${(v / 10) * 100}%` }} />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <button
                                onClick={() => { setStage('setup'); setPersona(null); setHistory([]); setSummary(null); }}
                                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2"
                            >
                                <RefreshCw size={16} /> Run another session
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // -- Active interview (asking / answering / reviewing) --
    return (
        <div className="min-h-screen bg-slate-100">
            <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-4">
                <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg">
                    <ArrowLeft size={20} />
                </button>
                <div className="flex items-center gap-2 flex-1">
                    <span className="text-2xl">{persona.emoji}</span>
                    <div>
                        <h1 className="font-bold text-slate-800 text-sm">{persona.name}</h1>
                        <p className="text-xs text-slate-500">Question {history.length + (stage === 'reviewing' ? 0 : 1)} of {MAX_QUESTIONS}</p>
                    </div>
                </div>
                <button
                    onClick={generateSummary}
                    className="text-xs text-slate-500 hover:text-slate-700"
                >
                    End early
                </button>
            </header>

            <div className="max-w-3xl mx-auto p-6">
                {/* Progress */}
                <div className="flex gap-1 mb-6">
                    {Array.from({ length: MAX_QUESTIONS }).map((_, i) => (
                        <div
                            key={i}
                            className={`flex-1 h-1.5 rounded-full ${i < history.length ? 'bg-purple-500' :
                                    i === history.length ? 'bg-purple-300' : 'bg-slate-200'
                                }`}
                        />
                    ))}
                </div>

                {/* Question card */}
                {currentQ && (
                    <div className="bg-white rounded-xl shadow-sm p-6 mb-4">
                        <div className="text-xs font-bold text-purple-600 uppercase mb-2 flex items-center gap-2">
                            <MessageCircle size={12} />
                            {currentQ.competency} {currentQ.isFollowUp ? '· follow-up' : ''}
                        </div>
                        <p className="text-lg text-slate-800 leading-relaxed mb-3">{currentQ.question}</p>
                        {currentQ.hint && stage === 'answering' && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800 flex items-start gap-2">
                                <Lightbulb size={14} className="shrink-0 mt-0.5" />
                                <span>{currentQ.hint}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Answer input */}
                {stage === 'answering' && (
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <textarea
                            value={answer}
                            onChange={(e) => setAnswer(e.target.value)}
                            rows={6}
                            placeholder="Type your answer (STAR + Reflection: Situation, Task, Action, Result, what you'd do differently)…"
                            className="w-full border border-slate-200 rounded-lg p-3 text-sm outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                            disabled={loading}
                        />
                        <div className="flex gap-2 mt-3">
                            <button
                                onClick={() => submitAnswer(true)}
                                disabled={loading}
                                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm flex items-center gap-1"
                            >
                                <SkipForward size={14} /> Skip
                            </button>
                            <button
                                onClick={() => submitAnswer(false)}
                                disabled={!answer.trim() || loading}
                                className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-bold py-2 rounded-lg flex items-center justify-center gap-2"
                            >
                                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                                Submit
                            </button>
                        </div>
                    </div>
                )}

                {/* Feedback card */}
                {stage === 'reviewing' && feedback && (
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-bold text-slate-800">Feedback</h3>
                            <div className="text-2xl font-black">
                                <span className={
                                    feedback.score >= 8 ? 'text-emerald-600' :
                                        feedback.score >= 5 ? 'text-amber-600' : 'text-red-600'
                                }>{feedback.score}</span>
                                <span className="text-sm text-slate-400">/10</span>
                            </div>
                        </div>

                        {feedback.strengths?.length > 0 && (
                            <div className="mb-3">
                                <p className="text-xs font-bold text-emerald-600 mb-1">What worked</p>
                                <ul className="text-sm text-slate-700 space-y-1">
                                    {feedback.strengths.map((s, i) => <li key={i}>✓ {s}</li>)}
                                </ul>
                            </div>
                        )}

                        {feedback.weaknesses?.length > 0 && (
                            <div className="mb-3">
                                <p className="text-xs font-bold text-red-600 mb-1">What to improve</p>
                                <ul className="text-sm text-slate-700 space-y-1">
                                    {feedback.weaknesses.map((s, i) => <li key={i}>✗ {s}</li>)}
                                </ul>
                            </div>
                        )}

                        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-3">
                            <p className="text-xs font-bold text-blue-800 mb-1">Model STAR + Reflection answer</p>
                            <p className="text-xs text-blue-900 leading-relaxed">{feedback.modelAnswer}</p>
                        </div>

                        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 mb-4 text-sm text-amber-900 flex items-start gap-2">
                            <Lightbulb size={14} className="shrink-0 mt-0.5" />
                            <span>{feedback.oneLineImprovement}</span>
                        </div>

                        <button
                            onClick={handleNext}
                            disabled={loading}
                            className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-bold py-2 rounded-lg flex items-center justify-center gap-2"
                        >
                            {loading ? <Loader2 size={16} className="animate-spin" /> :
                                history.length >= MAX_QUESTIONS ? <Trophy size={16} /> : <MessageCircle size={16} />
                            }
                            {history.length >= MAX_QUESTIONS ? 'See session report' : 'Next question'}
                        </button>
                    </div>
                )}

                {loading && stage !== 'reviewing' && (
                    <div className="text-center py-8">
                        <Loader2 className="animate-spin text-purple-600 mx-auto mb-2" size={32} />
                        <p className="text-sm text-slate-500">{persona.name} is thinking…</p>
                    </div>
                )}

                {error && <p className="text-xs text-red-600 mt-3">{error}</p>}
            </div>
        </div>
    );
};

export default MockInterview;
